import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { NoContent, ReactTable } from '../../components/shared';
import { CURRENCY_EXCHANGE_DYNAMIC } from '../../constants/tableColumns';
import { dateFormatForApi, isArrayWithLength } from '../../utils/common-methods';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useGetCurrencyExchangeQuery } from '../../services/ExchangeRate/ExchangeRateService';
import { useGetCurrenciesQuery } from '../../services/Currencies/CurrenciesService';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';
import { setExchangeRatesFilters } from '../../redux-slice/exchangeRatesFilters';

const ExchangeRateTable = ({ onTotalChange }) => {
  const theme = useTheme();
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'));
  const columns = useMemo(() => CURRENCY_EXCHANGE_DYNAMIC(isBelowMD), [isBelowMD]);
  const dispatch = useDispatch();

  const { exchangeRates } = useSelector((state) => state.exchangeRatesFilters || {});
  const tabFilters = exchangeRates || {
    pageNumber: DEFAULT_PAGE,
    rowsPerPage: DEFAULT_PER_PAGE,
    searchText: '',
  };

  const lastPageRef = useRef(tabFilters.pageNumber || DEFAULT_PAGE);
  const lastPerPageRef = useRef(tabFilters.rowsPerPage || DEFAULT_PER_PAGE);

  useEffect(() => {
    lastPageRef.current = tabFilters.pageNumber || DEFAULT_PAGE;
  }, [tabFilters.pageNumber]);

  useEffect(() => {
    lastPerPageRef.current = tabFilters.rowsPerPage || DEFAULT_PER_PAGE;
  }, [tabFilters.rowsPerPage]);

  const selectedCurrencies = tabFilters.currencyFilter || [];
  const selectedProvider = tabFilters.providerFilter || null;
  const selectedUpdatedBy = tabFilters.updatedByFilter || [];
  const { currentData: currenciesData = [] } = useGetCurrenciesQuery({ includeInactive: false });
  const activeCurrenciesData = useMemo(
    () => currenciesData.filter((currency) => currency?.isActive === true),
    [currenciesData],
  );

  //supports comma-separated currencies in `toCurrency`.
  const selectedCurrencyCodes = useMemo(() => {
    const codes = selectedCurrencies
      .map((selectedCurrency) => {
        const selectedCurrencyById = selectedCurrency?.id
          ? activeCurrenciesData.find((currency) => Number(currency?.id) === Number(selectedCurrency.id)) || null
          : null;

        return (
          selectedCurrencyById?.abbreviation ||
          selectedCurrency?.abbreviation ||
          selectedCurrency?.code ||
          selectedCurrency?.currencyCode ||
          selectedCurrency?.name ||
          ''
        );
      })
      .filter(Boolean);

    return [...new Set(codes)];
  }, [activeCurrenciesData, selectedCurrencies]);

  const toCurrency = selectedCurrencyCodes.join(',');

  const updatedByIds = useMemo(
    () =>
      selectedUpdatedBy
        .map((u) => u?.id)
        .filter(Boolean)
        .join(','),
    [selectedUpdatedBy],
  );

  // fetchedTimeframeRange is set by Add New page after calling the timeframe API.
  // dateRange is set by the filter drawer. They are independent; fetched range takes precedence
  // only until the filter drawer is explicitly applied (which clears fetchedTimeframeRange).
  const selectedFromDate = tabFilters.fetchedTimeframeRange?.from || tabFilters.dateRange?.from || '';
  const selectedToDate = tabFilters.fetchedTimeframeRange?.to || tabFilters.dateRange?.to || selectedFromDate;
  const provider = selectedProvider?.value || selectedProvider?.id || '';

  const queryParams = useMemo(
    () => ({
      page: tabFilters.pageNumber || DEFAULT_PAGE,
      perPage: tabFilters.rowsPerPage || DEFAULT_PER_PAGE,
      search: tabFilters.searchText || '',
      toCurrency,
      fromDate: selectedFromDate,
      toDate: selectedToDate,
      provider,
      updatedBy: updatedByIds,
    }),
    [
      tabFilters.pageNumber,
      tabFilters.rowsPerPage,
      tabFilters.searchText,
      toCurrency,
      selectedFromDate,
      selectedToDate,
      provider,
      updatedByIds,
    ],
  );

  const {
    data: apiResponse,
    error,
    isLoading,
  } = useGetCurrencyExchangeQuery(queryParams, { refetchOnMountOrArgChange: true });

  const exchangeRateData = useMemo(() => {
    if (!apiResponse?.data?.exchangeRates) return [];
    return apiResponse.data.exchangeRates.map((item) => {
      const { currency, exchangeRate, exchangeRateDate } = item;
      const updatedByUser = item?.updatedByUser || item?.updatedBy;
      const updatedByName =
        `${updatedByUser?.firstName || ''} ${updatedByUser?.lastName || ''}`.trim() ||
        item?.updatedByName ||
        updatedByUser?.name ||
        '';
      return {
        ...item,
        currencyCode: currency?.abbreviation || 'N/A',
        currencyName: currency?.name || 'N/A',
        exchangeRate: exchangeRate || 0,
        baseCurrency: 'USD',
        rateDate: exchangeRateDate ? dateFormatForApi(exchangeRateDate) : 'N/A',
        provider: item?.provider || 'N/A',
        updatedByName,
      };
    });
  }, [apiResponse?.data?.exchangeRates]);

  useEffect(() => {
    if (apiResponse?.data?.totalCount !== undefined) {
      onTotalChange(apiResponse.data.totalCount);
    }
  }, [apiResponse?.data?.totalCount, onTotalChange]);

  const pageChangeHandler = (newPage) => {
    if (Number(newPage) === Number(lastPageRef.current)) {
      return;
    }

    lastPageRef.current = newPage;
    dispatch(setExchangeRatesFilters({ ...tabFilters, pageNumber: newPage }));
  };

  const perPageChangeHandler = (newPerPage) => {
    if (Number(newPerPage) === Number(lastPerPageRef.current)) {
      return;
    }

    lastPerPageRef.current = newPerPage;
    lastPageRef.current = DEFAULT_PAGE;
    dispatch(setExchangeRatesFilters({ ...tabFilters, rowsPerPage: newPerPage, pageNumber: DEFAULT_PAGE }));
  };

  if (isLoading) return <LoadingSpinner containerHeight={300} />;

  if (error && !isArrayWithLength(exchangeRateData)) {
    return (
      <Container maxWidth="xl">
        <NoContent
          title="No Exchange Rate Data Found"
          desc={error?.data?.message || 'There is no exchange rate data available.'}
        />
      </Container>
    );
  }

  if (!isArrayWithLength(exchangeRateData)) {
    return (
      <Container maxWidth="xl">
        <NoContent title="No Exchange Rates" desc="No exchange rates found matching your search criteria." />
      </Container>
    );
  }

  return (
    <Box>
      <ReactTable
        data={exchangeRateData}
        columns={columns}
        pagination
        manualPagination
        totalCount={apiResponse?.data?.totalCount || 0}
        pageNumberForTablePagination={(tabFilters.pageNumber || 1) - 1}
        onPageChange={pageChangeHandler}
        onPerPageChange={perPageChangeHandler}
        perPage={tabFilters.rowsPerPage || DEFAULT_PER_PAGE}
      />
    </Box>
  );
};

export default ExchangeRateTable;
